import { createSlice } from '@reduxjs/toolkit';

const getInitialState = () => ({
  nameEditing: false,
  productBuilderData: null,
  topList: [],
  sizeList: [],
  heightList: [],
  patternsList: null,
  selectedSize: null,
  selectedHeight: null,
  selectedTopData: null,
  bottomList: [],
  selectedWallData: null,
  selectedBottomData: null,
  wallList: [],
  tapeList: [],
  labelList: [],
  handleList: [],
  buttonList: [],
  allBuilderPropList: [],
  isAllBuilderPropListFetched: {
    top: false,
    bottom: false,
    wall: false,
    tape: false,
    label: false,
    handle: false,
    layer: false,
    button: false,
    tuftPresets: false
  },
  changedValue: null,
  addedTapeData: [],
  addedHandleData: [],
  addedLabelData: [],
  selectionData: null,
  updateProductType: false,
  layerType: 'external',
  layerList: [],
  addedLayerData: [],
  addedViewData: null,
  productBuilderPropertyIds: {},
  silderUpdated: false,
  roomData: null,
  saveAsModal: false,
  allTopData: [],
  allBottomData: [],
  mirrorTopToggle: false,
  saveBuilderProjectLoading: false,
  loadedProjectId: null,
  instantiatedProps: [],
  allProperties: null,
  resetSizeData: null,
  oldPropsList: [],
  buttonData: {
    top: [],
    wall: [],
  },
  builderLoading: false,
  currentMattressView: "",
  canUpdateValues: true,
  tuftPresets: [],
  groupUngroupPopup: false,
  heightExceeded:null
});

const productBuilder = createSlice({
  name: 'productBuilder',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    setProductBuilderData: (state, { payload }) => {
      state.productBuilderData = payload;
    },
    setNameEditing: (state, { payload }) => {
      state.nameEditing = payload;
    },
    setTopList: (state, { payload }) => {
      state.topList = payload;
    },
    setSizeList: (state, { payload }) => {
      state.sizeList = payload;
    },
    setHeightList: (state, { payload }) => {
      state.heightList = payload;
    },
    setPatternList: (state, { payload }) => {
      state.patternsList = payload;
    },
    setSelectedSize: (state, { payload }) => {
      state.selectedSize = payload;
    },
    setSelectedHeight: (state, { payload }) => {
      state.selectedHeight = payload;
    },
    setSelectedTopData: (state, { payload }) => {
      state.selectedTopData = payload;
    },
    setBottomList: (state, { payload }) => {
      state.bottomList = payload;
    },
    setSelectedWallData: (state, { payload }) => {
      state.selectedWallData = payload;
    },
    setSelectedBottomData: (state, { payload }) => {
      state.selectedBottomData = payload;
    },
    setWallList: (state, { payload }) => {
      state.wallList = payload;
    },
    setTapeList: (state, { payload }) => {
      state.tapeList = payload;
    },
    setLabelList: (state, { payload }) => {
      state.labelList = payload;
    },
    setHandleList: (state, { payload }) => {
      state.handleList = payload;
    },
    setAllBuilderPropList: (state, { payload }) => {
      state.allBuilderPropList = payload;
    },
    setIsAllBuilderPropListFetched: (state, { payload }) => {
      if (!payload) {
        // If the payload is null or undefined, reset the state to its initial values.
        state.isAllBuilderPropListFetched = getInitialState()?.isAllBuilderPropListFetched;
      } else {
        // Otherwise, merge the new payload into the existing state for isAllBuilderPropListFetched.
        state.isAllBuilderPropListFetched = { ...state.isAllBuilderPropListFetched, ...payload };
      }
    },
    setChangedValue: (state, { payload }) => {
      state.changedValue = payload;
    },
    setAddedTapeData: (state, { payload }) => {
      state.addedTapeData = payload;
    },
    setAddedHandleData: (state, { payload }) => {
      state.addedHandleData = payload;
    },
    setAddedLabelData: (state, { payload }) => {
      state.addedLabelData = payload;
    },
    setSelectionData: (state, { payload }) => {
      state.selectionData = payload;
    },
    setUpdateProductType: (state, { payload }) => {
      state.updateProductType = payload;
    },
    setLayerType: (state, { payload }) => {
      state.layerType = payload;
    },
    setLayerList: (state, { payload }) => {
      state.layerList = payload;
    },
    setAddedLayerData: (state, { payload }) => {
      state.addedLayerData = payload;
    },
    setAddedViewData: (state, { payload }) => {
      state.addedViewData = payload;
    },
    setProductBuilderPropertyIds: (state, { payload }) => {
      state.productBuilderPropertyIds = payload;
    },
    setSilderUpdated: (state, { payload }) => {
      state.silderUpdated = payload;
    },
    setRoomData: (state, { payload }) => {
      state.roomData = payload;
    },
    setSaveAsModal: (state, { payload }) => {
      state.saveAsModal = payload;
    },
    setAllTopData: (state, { payload }) => {
      state.allTopData = payload;
    },
    setAllBottomData: (state, { payload }) => {
      state.allBottomData = payload;
    },
    setMirrorTopToggle: (state, { payload }) => {
      state.mirrorTopToggle = payload;
    },
    setSaveBuilderProjectLoading: (state, { payload }) => {
      state.saveBuilderProjectLoading = payload;
    },
    setLoadedProjectId: (state, { payload }) => {
      state.loadedProjectId = payload;
    },
    setInstantiatedProps: (state, { payload }) => {
      const data = [...state.instantiatedProps];
      data.push(payload);
      state.instantiatedProps = data;
    },
    setAllProperties: (state, { payload }) => {
      state.allProperties = payload;
    },
    setResetSizeData: (state, { payload }) => {
      state.resetSizeData = payload;
    },
    setOldPropsList: (state, { payload }) => {
      state.oldPropsList = payload;
    },
    setButtonList: (state, { payload }) => {
      state.buttonList = payload;
    },
    setButtonData: (state, { payload }) => {
      const { type, data } = payload;
      state.buttonData[type] = data;
    },
    setBuilderLoading: (state, { payload }) => {
      state.builderLoading = payload;
    },
    setCurrentMattressView: (state, { payload }) => {
      state.currentMattressView = payload;
    },
    setCanUpdateValues: (state, { payload }) => {
      state.canUpdateValues = payload;
    },
    setTuftPresets: (state, { payload }) => {
      state.tuftPresets = payload;
    },
    setGroupUngroupPopup: (state, { payload }) => {
      state.groupUngroupPopup = payload;
    },
    setMattressHeightExceeded: (state, { payload }) => {
      state.heightExceeded = payload;
    },
  },
});

export const productBuilderActions = productBuilder.actions;
export const productBuilderReducer = productBuilder.reducer;
export default productBuilderReducer;

export const selectGroupUngroupPopup = (state) => state.productBuilder.groupUngroupPopup;
export const selectHeightExceeded = (state) => state.productBuilder.heightExceeded;
export const selectTuftPresets = (state) => state.productBuilder.tuftPresets;
export const selectCanUpdateValues = (state) => state.productBuilder.canUpdateValues;
export const selectBuilderLoading = (state) => state.productBuilder.builderLoading;
export const selectButtonData = (state) => state.productBuilder.buttonData;
export const selectButtonList = (state) => state.productBuilder.buttonList;
export const selectOldPropsList = (state) => state.productBuilder.oldPropsList;
export const selectResetSizeData = (state) => state.productBuilder.resetSizeData;
export const selectAllProperties = (state) => state.productBuilder.allProperties;
export const selectInstantiatedProps = (state) => state.productBuilder.instantiatedProps;
export const selectLoadedProjectId = (state) => state.productBuilder.loadedProjectId;
export const selectMirrorTopToggle = (state) => state.productBuilder.mirrorTopToggle;
export const selectAllBottomData = (state) => state.productBuilder.allBottomData;
export const selectAllTopData = (state) => state.productBuilder.allTopData;
export const selectSaveAsModal = (state) => state.productBuilder.saveAsModal;
export const selectRoomData = (state) => state.productBuilder.roomData;
export const selectSilderUpdated = (state) => state.productBuilder.silderUpdated;
export const selectProductBuilderData = (state) => state.productBuilder.productBuilderData;
export const selectNameEditing = (state) => state.productBuilder.nameEditing;
export const selectTopList = (state) => state.productBuilder.topList;
export const selectSizeList = (state) => state.productBuilder.sizeList;
export const selectHeightList = (state) => state.productBuilder.heightList;
export const selectPatternList = (state) => state.productBuilder.patternsList;
export const selectSelectedSize = (state) => state.productBuilder.selectedSize;
export const selectSelectedHeight = (state) => state.productBuilder.selectedHeight;
export const selectBottomList = (state) => state.productBuilder.bottomList;
export const selectSelectedTopData = (state) => state.productBuilder.selectedTopData;
export const selectSelectedWallData = (state) => state.productBuilder.selectedWallData;
export const selectSelectedBottomData = (state) => state.productBuilder.selectedBottomData;
export const selectWallList = (state) => state.productBuilder.wallList;
export const selectTapeList = (state) => state.productBuilder.tapeList;
export const selectLabelList = (state) => state.productBuilder.labelList;
export const selectHandleList = (state) => state.productBuilder.handleList;
export const selectAllBuilderPropList = (state, showHidden = false) => {
  const top = selectTopList(state) || [];
  const bottom = selectBottomList(state) || [];
  const wall = selectWallList(state) || [];
  const tape = selectTapeList(state) || [];
  const label = selectLabelList(state) || [];
  const handle = selectHandleList(state) || [];
  const layer = selectLayerList(state) || [];
  const button = selectButtonList(state) || [];
  // Combine separate lists (top, bottom, wall, tape, label, handle, layer) into a single props array.
  let props = [...top, ...bottom, ...wall, ...tape, ...label, ...handle, ...layer, ...button];
  if (!showHidden) {
    props = props?.filter(val => !val?.is_hide);
  }
  // Check if all data has been fetched using the isAllBuilderPropListFetched state.
  const isFetched = Object.values(state.productBuilder.isAllBuilderPropListFetched).every(
    (a) => a === true
  );

  // If all data is fetched, return the combined props; otherwise, return an empty array.
  if (isFetched) return props;
  return [];
};
export const selectIsAllBuilderPropListFetched = (state) => {
  const isFetched = Object.values(state.productBuilder.isAllBuilderPropListFetched).every(
    (a) => a === true
  );
  return isFetched;
};
export const selectChangeValue = (state) => state.productBuilder.changedValue;
export const selectAddedTapeData = (state) => state.productBuilder.addedTapeData;
export const selectAddedLabelData = (state) => state.productBuilder.addedLabelData;
export const selectAddedHandleData = (state) => state.productBuilder.addedHandleData;
export const selectSelectionsData = (state) => state.productBuilder.selectionData;
export const selectUpdateProductType = (state) => state.productBuilder.updateProductType;
export const selectLayerType = (state) => state.productBuilder.layerType;
export const selectLayerList = (state) => state.productBuilder.layerList;
export const selectAddedLayerData = (state) => state.productBuilder.addedLayerData;
export const selectAddedViewData = (state) => state.productBuilder.addedViewData;
export const selectCurrentMattressView = (state) => state.productBuilder.currentMattressView;
export const selectProductBuilderPropertyIds = (state) =>
  state.productBuilder.productBuilderPropertyIds;
export const selectSaveBuilderProjectLoading = (state) =>
  state.productBuilder.saveBuilderProjectLoading;
